import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';

// Cache websocket capability by session to avoid repeated failing connects.
const realtimeSupportCache = new Map<string, boolean>();
const MAX_CACHE_SIZE = 100;

/**
 * Simple hook to subscribe to realtime session events via WebSocket.
 * Returns the last message received for processing by the component.
 */
export function useRealtimeSession(sessionId: string) {
  const isDev = import.meta.env.DEV;
  const realtimeFeatureEnabled =
    isDev ||
    (typeof window !== 'undefined' && (window as any).__TASKING_ENABLE_REALTIME__ === true);
  const { authState } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<any>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const isUnmountedRef = useRef(false);

  const user = authState.type === 'authenticated' ? authState.user : null;
  const enabled = realtimeFeatureEnabled && !!sessionId && !!user;

  const connect = useCallback(async () => {
    if (!enabled || !user) return;

    const cachedSupport = realtimeSupportCache.get(sessionId);
    if (cachedSupport === false) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const httpProtocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
    const query = `userId=${encodeURIComponent(String(user.id))}&userName=${encodeURIComponent(user.displayName || 'User')}${user.avatarUrl ? `&avatarUrl=${encodeURIComponent(user.avatarUrl)}` : ''}`;
    const wsUrl = `${protocol}//${window.location.host}/_api/session/${sessionId}/realtime?${query}`;
    const probeUrl = `${httpProtocol}//${window.location.host}/_api/session/${sessionId}/realtime?${query}`;

    // Probe endpoint once so we don't create a websocket that immediately fails with 404.
    if (cachedSupport === undefined) {
      try {
        const probeResp = await fetch(probeUrl, {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json,text/plain,*/*' },
        });
        if (probeResp.status === 404) {
          // Evict oldest entries if cache is full
          if (realtimeSupportCache.size >= MAX_CACHE_SIZE) {
            const firstKey = realtimeSupportCache.keys().next().value;
            realtimeSupportCache.delete(firstKey);
          }
          realtimeSupportCache.set(sessionId, false);
          return;
        }
        // Evict oldest entries if cache is full
        if (realtimeSupportCache.size >= MAX_CACHE_SIZE) {
          const firstKey = realtimeSupportCache.keys().next().value;
          realtimeSupportCache.delete(firstKey);
        }
        realtimeSupportCache.set(sessionId, true);
      } catch {
        // Network probe failures are inconclusive; allow one websocket attempt.
      }
    }

    // Don't reconnect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      if (isDev) console.log('[RealtimeSession] Connected');
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setLastMessage(data);
      } catch (e) {
        // Ignore parse errors
      }
    };

    ws.onerror = () => {
      // Non-fatal: session may not exist or realtime not set up (e.g. 404 / no backend).
      // Keep production console quiet; only log this in development.
      if (isDev) {
        console.warn('[RealtimeSession] WebSocket error (session may not have realtime)');
      }
    };

    ws.onclose = (event) => {
      if (isDev) console.log('[RealtimeSession] Disconnected:', event.code);
      setIsConnected(false);
      wsRef.current = null;

      // Don't retry on "not found" / policy / abnormal — avoids error loop when session has no realtime
      const noRetryCodes = [1000, 1006, 1008, 1003];
      const shouldRetry =
        enabled &&
        !noRetryCodes.includes(event.code) &&
        reconnectAttemptsRef.current < maxReconnectAttempts;

      if (event.code === 1006) {
        // In this app a 1006 at connect-time is usually endpoint unavailable; cache as unsupported.
        // Evict oldest entries if cache is full
        if (realtimeSupportCache.size >= MAX_CACHE_SIZE) {
          const firstKey = realtimeSupportCache.keys().next().value;
          realtimeSupportCache.delete(firstKey);
        }
        realtimeSupportCache.set(sessionId, false);
      }

      if (shouldRetry) {
        const delay = Math.min(3000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          // Check unmount status INSIDE the callback to prevent race conditions
          if (isUnmountedRef.current) return;
          void connect();
        }, delay);
      }
    };
  }, [enabled, sessionId, user, isDev]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect on mount
  useEffect(() => {
    isUnmountedRef.current = false;
    if (enabled) {
      void connect();
    }
    return () => {
      isUnmountedRef.current = true;
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Heartbeat to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isConnected]);

  return {
    isConnected,
    lastMessage,
  };
}
